import type { SlaMetric, SlaState } from "@sagot/constants";
import type { useTranslation } from "@sagot/i18n";
import { resolveSlaDisplay } from "@sagot/ui";

type TFunction = ReturnType<typeof useTranslation>["t"];

/** One clock of a ticket (first reply, resolution…) as the API reports it, plus the texts the caller already formatted. */
export interface ServiceSlaLine {
  metric: SlaMetric;
  state: SlaState;
  /** Due time in the workspace time zone ("Thu 08:57"); formatting is the caller's, so dates stay in one place. */
  due?: string;
  /** "43 min", "1 business day 2 h". */
  detail?: string;
  /** 0..100 share of the window still left. */
  percentLeft?: number | null;
  /** Why the clock stopped ("waiting for client"). */
  pausedReason?: string;
}

/** The words of one SLA line: "First reply met", "Resolution due Thu 08:57", "Paused: waiting for client". */
export function slaLineText(t: TFunction, line: ServiceSlaLine): string {
  const metric = t(`service.chips.sla.metric.${line.metric}`);
  switch (resolveSlaDisplay(line.state, line.percentLeft)) {
    case "met":
      return t("service.chips.sla.met", { metric });
    case "breached":
      return t("service.chips.sla.breached", { metric });
    case "paused":
      return line.pausedReason ? t("service.chips.sla.pausedFor", { reason: line.pausedReason }) : t("service.chips.sla.paused", { metric });
    case "none":
      return t("service.chips.sla.none", { metric });
    case "ok":
      return line.due ? t("service.chips.sla.due", { metric, due: line.due }) : t("service.chips.sla.onTrack", { metric });
    default:
      return line.due ? t("service.chips.sla.due", { metric, due: line.due }) : t("service.chips.sla.dueSoon", { metric });
  }
}
